import { useState } from 'react';
import { SupplierThread } from '../components/forms/outreach/types';
import { bomClient } from '../client/bomClient';
import { apiErrorMessage } from '../utils/apiErrorMessage';

/**
 * Persists a buyer-entered contact email onto a thread's supplier candidate.
 * Split from useSupplierOutreach so the outreach hook stays focused on
 * threads and sending.
 */
export const useSupplierContactEmail = (
  bomId: string | null,
  onSaved: () => Promise<void> | void,
) => {
  const [isSavingEmail, setIsSavingEmail] = useState(false);
  const [emailSaveError, setEmailSaveError] = useState<string | null>(null);

  const saveContactEmail = async (thread: SupplierThread | undefined, email: string): Promise<boolean> => {
    if (!thread || isSavingEmail) return false;

    const trimmed = email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(trimmed)) {
      setEmailSaveError('Enter a valid email address (e.g. sales@supplier.com).');
      return false;
    }
    if (!bomId || !thread.componentId) {
      setEmailSaveError('Cannot resolve this supplier\'s BOM entry. Reload and try again.');
      return false;
    }

    setEmailSaveError(null);
    setIsSavingEmail(true);
    try {
      await bomClient.updateSupplierContactEmail(bomId, thread.componentId, thread.supplierName, trimmed);
      await onSaved();
      return true;
    } catch (error: any) {
      console.error('Error saving supplier contact email:', error);
      setEmailSaveError(apiErrorMessage(error, 'Failed to save the contact email.'));
      return false;
    } finally {
      setIsSavingEmail(false);
    }
  };

  return { saveContactEmail, isSavingEmail, emailSaveError };
};
